import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getFullTimestampLabel } from '../utils/chatDateLabel';

interface TimestampWithTooltipProps {
    /** Visible short time (existing displayTime). */
    time: string;
    /** Epoch ms; used for full datetime tooltip when valid. */
    createdBy?: number;
    className?: string;
}

const VIEWPORT_PADDING = 8;
const TRIGGER_GAP = 6;

type TooltipCoords = {
    top: number;
    left: number;
};

/**
 * Positions the tooltip inside the viewport using getBoundingClientRect:
 * prefers above the timestamp, flips below when needed, clamps horizontally.
 */
function computeTooltipCoords(
    trigger: DOMRect,
    tipWidth: number,
    tipHeight: number
): TooltipCoords {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = trigger.top - tipHeight - TRIGGER_GAP;
    if (top < VIEWPORT_PADDING) {
        top = trigger.bottom + TRIGGER_GAP;
        if (top + tipHeight > vh - VIEWPORT_PADDING) {
            top = Math.max(VIEWPORT_PADDING, vh - VIEWPORT_PADDING - tipHeight);
        }
    }

    let left = trigger.left + trigger.width / 2 - tipWidth / 2;
    if (left < VIEWPORT_PADDING) {
        left = VIEWPORT_PADDING;
    }
    if (left + tipWidth > vw - VIEWPORT_PADDING) {
        left = Math.max(VIEWPORT_PADDING, vw - VIEWPORT_PADDING - tipWidth);
    }

    return { top, left };
}

/**
 * Renders the visible timestamp with a styled CSS hover tooltip
 * showing the full datetime from createdBy.
 * Uses fixed positioning + collision detection so the tooltip is not clipped
 * by chat overflow containers.
 */
const TimestampWithTooltip: React.FC<TimestampWithTooltipProps> = ({
    time,
    createdBy,
    className,
}) => {
    const fullLabel = getFullTimestampLabel(createdBy);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const tipRef = useRef<HTMLSpanElement>(null);
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState<TooltipCoords | null>(null);

    const updatePosition = useCallback(() => {
        const triggerEl = triggerRef.current;
        const tipEl = tipRef.current;
        if (!triggerEl || !tipEl) {
            return;
        }

        const triggerRect = triggerEl.getBoundingClientRect();
        const tipWidth = tipEl.offsetWidth;
        const tipHeight = tipEl.offsetHeight;
        if (tipWidth === 0 || tipHeight === 0) {
            return;
        }

        setCoords(computeTooltipCoords(triggerRect, tipWidth, tipHeight));
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }
        updatePosition();
    }, [open, fullLabel, updatePosition]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const handleReposition = () => {
            updatePosition();
        };

        // Capture scroll from nested chat containers as well as window.
        window.addEventListener('scroll', handleReposition, true);
        window.addEventListener('resize', handleReposition);

        return () => {
            window.removeEventListener('scroll', handleReposition, true);
            window.removeEventListener('resize', handleReposition);
        };
    }, [open, updatePosition]);

    if (!fullLabel) {
        return <span className={className}>{time}</span>;
    }

    const showTooltip = () => setOpen(true);
    const hideTooltip = () => {
        setOpen(false);
        setCoords(null);
    };

    return (
        <span
            ref={triggerRef}
            className={`inline-block ${className ?? ''}`}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onFocus={showTooltip}
            onBlur={hideTooltip}
        >
            <span className="cursor-default">{time}</span>
            {open &&
                createPortal(
                    <span
                        ref={tipRef}
                        role="tooltip"
                        className="pointer-events-none whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-[11px] font-normal leading-tight text-white shadow-md"
                        style={{
                            position: 'fixed',
                            top: coords ? coords.top : 0,
                            left: coords ? coords.left : 0,
                            zIndex: 9999,
                            // Hide until measured to avoid a one-frame flash at (0,0).
                            visibility: coords ? 'visible' : 'hidden',
                        }}
                    >
                        {fullLabel}
                    </span>,
                    document.body
                )}
        </span>
    );
};

export default TimestampWithTooltip;
